import { FC, useState, useRef, useEffect } from 'react';
import { ReactMultiEmail, isEmail } from 'react-multi-email';
import 'react-multi-email/dist/style.css';
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'

import {Modal} from '../../../components/Modal'

import {UserType} from '../../../models'
import {toTitleCase} from '../../../utils'
import { adminAPI } from '../../../api'

interface Props {
  show: boolean
  handleUpdated: () => void
  handleClose: () => void
}

const schema = Yup.object().shape({
  emails: Yup.array()
    .of(Yup.string().email('Invalid email format'))
    .min(1, 'At least one email is required')
    .required('Required'),
  type: Yup.string().required('Required')
});


const UserTypeUpdateModal: FC<Props> = ({ show, handleUpdated, handleClose }) => {
  
  const [handling, setHandling] = useState(false)

  const [emails, setEmails] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEmails([])
  }, [show]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.height = 'auto'; // Reset height
      containerRef.current.style.height = `${containerRef.current.scrollHeight}px`; // Set to scroll height
    }
  }, [emails]);

  const formik = useFormik({
    initialValues: {"emails": [], 'type':''},
    validationSchema: schema,
    onSubmit: async (values) => {
      setHandling(true)
      try{
        await adminAPI.updateUserType(values)
        formik.resetForm()
        setEmails([])
        handleUpdated()
        handleClose()
      } finally {
        setHandling(false)
      }
    }
  })

  return <Modal show={show} handleClose={handleClose} dialogClassName='' title="Update User Type (Only registered users)">
      <form
        className='form fv-plugins-bootstrap5 fv-plugins-framework'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className='fv-row mb-5 form-floating'>
          <div className={clsx(
            'react-multi-email-container form-control border border-gray-400 min-h-60px d-flex align-items-center mh-300px overflow-auto',
            {'is-invalid': emails.length == 0 },
            {
              'is-valid':  emails.length > 0,
            }
          )}
          ref={containerRef}>
          <ReactMultiEmail
              emails={emails}
              onChange={(_emails: string[]) => {
                setEmails(_emails)
                formik.setFieldValue('emails', _emails); 
              }}
              validateEmail={email => isEmail(email)}
              getLabel={(email, index, removeEmail) => {
                return (
                  <span key={index} className="badge badge-pill badge-primary me-2 mb-1">
                    {email}
                    <span
                      className="ms-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeEmail(index)}
                    >
                      &times;
                    </span>
                  </span>
                );
              }}
              className={clsx(
                'bg-transparent border-0 ',
                {'p-0': emails.length == 0 },
                {'pt-2 pb-0 px-0': emails.length > 0 },
              )}
            />
            </div>
          <label className='form-label fs-6 text-gray-600'>Emails</label>
        </div>
        <div className='fv-row mb-5 form-floating'>
          <select
            {...formik.getFieldProps('type')}
            className={clsx(
              'form-control form-select h-60px border border-gray-400',
              {'is-invalid': formik.touched.type && formik.errors.type},
              {
                'is-valid': formik.touched.type && !formik.errors.type,
              }
            )}
          >
            <option value="" selected>Select</option>
            {Object.values(UserType).map((type) => (
              <option key={type} value={type}>{toTitleCase(type)}</option>
            ))}
          </select>
          <label className='form-label fs-6 text-gray-600'>Type</label>
        </div>
        <div className="text-center">
          <button
            type='submit'
            className="btn btn-primary"
            disabled={handling || !formik.isValid}
          >
            {!handling && <span className='indicator-label'>Update</span>}
            {handling && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          
          <button type="button" className="btn btn-light ms-3" onClick={handleClose}>
            Close
          </button>
        </div>
      </form>
    </Modal>
  }
  
  export {UserTypeUpdateModal}